import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { extractError } from "../lib/errors";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { PageHeader } from "../components/PageHeader";
import { FW_COLORS } from "../constants/frameworks";

// Reference framework IDs — always-on, never consume cap (§4.3 diseño S17b).
// Module-level constant so useMemo deps array can omit it safely.
const REFERENCE_IDS = new Set(["nist_csf_2", "iso_31000", "iso_22301"]);

// Default selection displayed (NOT persisted) when org has no saved selection yet.
// See ADR-S17b-001: active_frameworks=[] means "show all" (backfill), but the UI
// pre-populates a reasonable starting set so the admin sees something sensible.
const TIER_DEFAULTS = {
  consultor:   ["iso27001"],
  profesional: ["iso27001", "nist_scf", "cis_v8"],
  empresarial: ["iso27001", "nist_scf", "cis_v8", "iso20000", "nortic_a7", "nortic_a8"],
};

const GROUP_ORDER = ["core", "rd_latam", "sectorial", "zona_franca", "reference"];

// ----------------------------------------------------------------
// Sub-component: a single framework row
// ----------------------------------------------------------------
const FrameworkRow = ({ item, checked, onToggle, readOnly }) => {
  const { t } = useI18n();
  const color = FW_COLORS[item.id] || "#64748b";
  const isDisabled = readOnly || !item.selectable || item.always_on;

  const lockedReason = item.locked_reason_es || item.locked_reason_en || null;

  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        {/* Color dot */}
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: color }}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{item.label}</span>
            {item.always_on && (
              <Badge variant="outline" className="text-[10px] py-0 h-4 shrink-0">
                {t("settings.frameworks.locked_reference_badge")}
              </Badge>
            )}
            {!item.selectable && !item.always_on && (
              <Lock className="w-3 h-3 text-muted-foreground shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{item.category}</p>
          {lockedReason && !item.always_on && (
            <p className="text-xs text-muted-foreground/60 mt-0.5 flex items-center gap-1">
              <Info className="w-3 h-3 shrink-0" />
              {lockedReason}
            </p>
          )}
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={() => onToggle(item.id, item.selectable)}
        disabled={isDisabled}
        className="shrink-0"
      />
    </div>
  );
};

// ----------------------------------------------------------------
// Sub-component: a group section (core / rd_latam / zona_franca / reference)
// ----------------------------------------------------------------
const FrameworkGroupSection = ({ group, items, selected, onToggle, readOnly }) => {
  const { t } = useI18n();

  const groupTitleKey = `settings.frameworks.group_${group}`;

  if (!items || items.length === 0) return null;

  return (
    <div className="space-y-1">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground pt-4 pb-1">
        {t(groupTitleKey)}
      </h3>
      <div>
        {items.map((item) => (
          <FrameworkRow
            key={item.id}
            item={item}
            checked={item.always_on || selected.has(item.id)}
            onToggle={onToggle}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------
// Main page component
// ----------------------------------------------------------------
const FrameworkPicker = () => {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const { refresh: refreshOrg } = useOrg();

  const [data, setData] = useState(null);   // shape: {active_frameworks, cap, used, tier_id, catalog}
  const [selected, setSelected] = useState(new Set());
  const [originalSelected, setOriginalSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/organizations/current/frameworks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      setData(d);

      // If no selection stored yet, pre-populate by tier (view-only, not persisted).
      let initSet;
      if (!d.active_frameworks || d.active_frameworks.length === 0) {
        const defaults = TIER_DEFAULTS[d.tier_id] || [];
        initSet = new Set(defaults);
      } else {
        initSet = new Set(d.active_frameworks);
      }
      setSelected(initSet);
      setOriginalSelected(new Set(initSet));
    } catch (e) {
      setError(extractError(e, t("settings.frameworks.load_failed")));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Whether the current user can edit
  const isAdmin = ["owner", "admin"].includes(user?.role);

  // Number of billable (non-reference) frameworks selected
  const billableSelected = useMemo(
    () => [...selected].filter((id) => !REFERENCE_IDS.has(id)).length,
    [selected]
  );
  const overCap = data ? billableSelected > data.cap : false;

  // Dirty: selection differs from the snapshot taken at mount / last save.
  // D-S17-015: compare against originalSelected (which captures pre-population
  // state) instead of data.active_frameworks (which is empty during tier
  // pre-population and caused the button to enable spuriously on first render).
  const isDirty = useMemo(() => {
    if (!originalSelected) return false;
    if (originalSelected.size !== selected.size) return true;
    for (const id of selected) {
      if (!originalSelected.has(id)) return true;
    }
    return false;
  }, [selected, originalSelected]);

  // Group catalog items by their group key
  const groupedCatalog = useMemo(() => {
    if (!data?.catalog) return {};
    const groups = {};
    for (const item of data.catalog) {
      const g = item.group || "core";
      if (!groups[g]) groups[g] = [];
      groups[g].push(item);
    }
    return groups;
  }, [data]);

  const onToggle = useCallback((fwId, selectable) => {
    if (!selectable || !isAdmin) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fwId)) {
        next.delete(fwId);
      } else {
        next.add(fwId);
      }
      return next;
    });
  }, [isAdmin]);

  const onSave = async () => {
    if (overCap || !isAdmin) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/organizations/current/frameworks`,
        { frameworks: [...selected] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("settings.frameworks.saved"));
      await refreshOrg();
      // Refresh local data so isDirty resets correctly
      await fetchData();
    } catch (e) {
      toast.error(extractError(e, t("settings.frameworks.save_failed")));
    } finally {
      setSaving(false);
    }
  };

  // ── Render states ──

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const cap = data?.cap ?? 0;
  const tierId = data?.tier_id || "";

  return (
    <div className="p-6 space-y-6" data-testid="framework-picker-page">
      <PageHeader
        title={t("settings.frameworks.title")}
        description={t("settings.frameworks.subtitle")}
        data-testid="framework-picker-header"
      />

      <Card>
        {/* Header row: counter + tier badge */}
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">
                <span className={overCap ? "text-destructive font-bold" : "font-semibold"}>
                  {billableSelected}
                </span>
                <span className="text-muted-foreground font-normal">
                  {" "}/ {cap} {t("settings.frameworks.counter_suffix")}
                </span>
              </CardTitle>
              {overCap && (
                <Badge variant="destructive" className="text-xs">
                  {t("settings.frameworks.over_cap_badge")}
                </Badge>
              )}
            </div>
            {tierId && (
              <Badge variant="outline" className="capitalize text-xs">
                {tierId}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Read-only banner for members */}
          {!isAdmin && (
            <Alert className="mb-4">
              <Info className="w-4 h-4" />
              <AlertDescription>
                {t("settings.frameworks.read_only")}
              </AlertDescription>
            </Alert>
          )}

          {/* Over-cap warning */}
          {overCap && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {t("settings.frameworks.over_cap", {
                  excess: billableSelected - cap,
                  cap,
                })}{" "}
                <Link to="/billing" className="underline font-medium">
                  {t("settings.frameworks.upgrade_cta")}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {/* Framework groups */}
          <div className="divide-y divide-border">
            {GROUP_ORDER.map((group) => (
              <FrameworkGroupSection
                key={group}
                group={group}
                items={groupedCatalog[group] || []}
                selected={selected}
                onToggle={onToggle}
                readOnly={!isAdmin}
              />
            ))}
          </div>
        </CardContent>

        <CardFooter className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {t("settings.frameworks.help_text")}
          </p>
          <div className="flex items-center gap-2">
            <Link
              to="/billing"
              className="text-xs text-primary flex items-center gap-1 hover:underline"
            >
              {t("settings.frameworks.billing_link")}
              <ExternalLink className="w-3 h-3" />
            </Link>
            <Button
              onClick={onSave}
              disabled={!isDirty || overCap || saving || !isAdmin}
              size="sm"
            >
              {saving
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("common.loading")}</>
                : t("common.save")}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default FrameworkPicker;
